import { ol_VectorLayer, ol_Coordinate, ol_extent, ol_Feature, ol_Map, ol_MapBrowserEvent, ol_source, ol_geom, ol_layer } from "@omnia/exports/openlayers"
import Coord = omnia.map.Coord
import TransformInteraction = omnia.map.TransformInteraction


export interface OrientationInteractionTarget {
    getGridFeature(): ol_Feature
    getOrientation(): number
    setOrientation(value: number)
    setOffset(offset: [number, number])
}

export class OrientationInteraction {

    private interaction: InteractionImpl


    constructor(private olmap: ol_Map) {
    }

    dispose() {
        if (this.interaction) {
            this.interaction.dispose()
            this.interaction = null
        }
    }

    setup(boundary: ol_geom.Geometry, centroid: Coord, target: OrientationInteractionTarget) {
        this.dispose()
        this.interaction = new InteractionImpl(this.olmap, boundary, centroid, target)
    }

    refresh() {
        if (this.interaction) {
            this.interaction.refresh()
        }
    }

}

class InteractionImpl extends TransformInteraction {

    private _localLayer: ol_VectorLayer
    private _localFeature: ol_Feature

    private _radius: number
    private _lastOverlayPosition: ol_Coordinate

    private _gridDragOrigin: ol_Coordinate
    private _gridDragOffset: [number, number]

    constructor(
        private olmap: ol_Map,
        private boundary: ol_geom.Geometry,
        private centroid: Coord,
        private target: OrientationInteractionTarget) {

        super()
        this.setup()

    }

    dispose() {

        this._gridDragOffset = null
        this._gridDragOrigin = null

        if (this._localLayer) {
            this.olmap.removeLayer(this._localLayer)
            this._localLayer = null
        }

        this.setFeature(null)
        this.setActive(false)
        this.olmap.removeInteraction(this)

    }

    setup() {

        this._allowResize = false
        this._allowTranslate = false
        this._watchFeature = false

        const extent = this.boundary.getExtent()
        const size = ol_extent.getSize(extent)

        const width = size[0] / 4
        const height = size[1] / 4
        this._radius = Math.sqrt((width * width) + (height * height))

        const feature = this._localFeature = new ol_Feature()

        this.updateFeatureGeometry()

        this._localLayer = new ol_layer.Vector({
            source: new ol_source.Vector({
                features: [feature]
            }),
            style: omnia.map.styles.getBaseEditStyle(null)
        })

        this.olmap.addLayer(this._localLayer)
        this.olmap.addInteraction(this)

        this.setFeature(feature, false)
        this.setActive(true)

    }

    refresh() {
        this._lastOverlayPosition = null
        this.layoutOverlay()
    }


    setupOverlay() {
        super.setupOverlay()
        this._rotationOverlay.overlay.setOffset([0, 0])
    }

    protected layoutOverlay() {
        super.layoutOverlay()
        this.updateFeatureGeometry()
    }

    protected getDefaultOverlayPosition(): ol_Coordinate {

        if (this._lastOverlayPosition) {
            return this._lastOverlayPosition
        }

        const angle = this.target.getOrientation() / (180 / Math.PI)

        const pos = [
            this.centroid[0] + (this._radius * Math.cos(angle)),
            this.centroid[1] + (this._radius * Math.sin(angle))
        ]

        return pos

    }

    protected handleDownEvent(evt: ol_MapBrowserEvent): boolean {

        this._gridDragOrigin = null

        if (super.handleDownEvent(evt)) {
            return true
        }

        const feature = this.target && this.target.getGridFeature()

        if (feature) {
            const geom: any = feature.getGeometry()
            if (geom && geom.intersectsCoordinate(evt.coordinate)) {
                this._gridDragOrigin = evt.coordinate
                return true
            }
        }

    }

    protected handleDragEvent(evt: ol_MapBrowserEvent) {

        if (this._drag) {
            return super.handleDragEvent(evt)
        }

        if (this.target) {
            const offset = this.getDragOffset(evt)
            if (offset) {
                this.target.setOffset(offset)
            }
        }

    }

    protected handleUpEvent(evt: ol_MapBrowserEvent) {

        super.handleDragEvent(evt)

        if (this._gridDragOrigin) {
            const offset = this.getDragOffset(evt)
            this._gridDragOffset = offset
        }

        return undefined as boolean

    }

    private getDragOffset(evt: ol_MapBrowserEvent) {

        const committed = this._gridDragOffset || [0, 0]

        if (this._gridDragOrigin) {

            const offset: [number, number] = [
                committed[0] + this._gridDragOrigin[0] - evt.coordinate[0],
                committed[1] + this._gridDragOrigin[1] - evt.coordinate[1]]

            return offset

        }

    }

    protected transform(translation: number[], scale: number, rotationAngle: number, dragCoord: ol_Coordinate) {

        const d = rotationAngle * (180 / Math.PI)
        this.target.setOrientation(d)

        this._lastOverlayPosition = dragCoord

        const w = dragCoord[0] - this.centroid[0]
        const h = dragCoord[1] - this.centroid[1]

        this._radius = Math.sqrt((w * w) + (h * h))

        this.layoutOverlay()

    }

    private updateFeatureGeometry() {
        const pos = this.getDefaultOverlayPosition()
        const line = new ol_geom.LineString([this.centroid, pos])
        this._localFeature.setGeometry(line)
    }

    protected updateCursor(map: ol_Map, cursor: string) {
        // The cursor should not change with this interaction
    }

    protected getGeomCenter(geom: ol_geom.Geometry) {
        return this.centroid
    }

    protected getDragRotateOrigin() {
        return [this.centroid[0] + this._radius, this.centroid[1]]
    }

}
